// Contact person forms need two things the shared TDAForm does not do out of
// the box (kept app-local by decision — no library release in this round):
//
// 1. Proposal chips for the free-text `role`: the backend ships client-specific
//    proposals as `optionValues` on a plain text attribute, which TDAForm
//    ignores. The attribute is rewritten to PROPOSALS_INPUT_TYPE and rendered
//    by a custom input (see contactPersonInputRenderers).
// 2. A read-only presentation for linked contacts (sourceKind != OWN): the
//    entity-level TDAs mark firstName/lastName/email/phone as `disabled` and
//    carry the resolved value in `inheritedValue`. The form shows that resolved
//    value — the same the contact card shows — instead of the stored column.

export const PROPOSALS_INPUT_TYPE = 'textWithProposals';
export const LINKED_READ_ONLY_INPUT_TYPE = 'linkedReadOnly';

export interface TdaOptionValue {
    value: string | number | boolean | null;
    name?: string | null;
}

export interface FormAttribute {
    index: string;
    inputType: string;
    name?: string;
    disabled?: boolean | null;
    value?: unknown;
    optionValues?: (TdaOptionValue | null)[] | null;
    inheritedValue?: { value?: unknown; displayValue?: unknown } | null;
    rules?: unknown[] | null;
    [key: string]: unknown;
}

export interface Proposal {
    value: string;
    name: string;
}

export const hasProposals = (attribute: FormAttribute): boolean =>
    attribute.inputType === 'text'
    && Array.isArray(attribute.optionValues)
    && attribute.optionValues.some(Boolean);

/** Proposal chips of a text attribute: `value` is what gets stored, `name` what is shown. */
export const getProposals = (attribute: FormAttribute): Proposal[] =>
    (attribute.optionValues ?? [])
        .filter((option): option is TdaOptionValue => !!option && option.value !== null && option.value !== undefined)
        .map((option) => ({
            value: String(option.value),
            name: option.name ? String(option.name) : String(option.value),
        }));

/**
 * Rewrites contact person TDAs for the app's custom inputs. Order matters: a
 * linked read-only field wins over anything else about the attribute.
 */
export const prepareContactPersonAttributes = <T extends FormAttribute>(
    attributes: (T | null | undefined)[] | null | undefined
): T[] =>
    (attributes ?? [])
        .filter((attribute): attribute is T => !!attribute)
        .map((attribute) => {
            if (attribute.disabled && attribute.inheritedValue) {
                const {displayValue, value: inheritedRaw} = attribute.inheritedValue;
                return {
                    ...attribute,
                    inputType: LINKED_READ_ONLY_INPUT_TYPE,
                    // resolved value, like the card; '' keeps the input controlled
                    value: displayValue ?? inheritedRaw ?? attribute.value ?? '',
                    // dropped so TDAForm's own inherited branch (with its "set
                    // here" override) never kicks in for this attribute
                    inheritedValue: null,
                    // the backend ignores the field on a linked contact, so its
                    // rules must not be able to block the submit
                    rules: [],
                };
            }
            if (hasProposals(attribute)) {
                return {...attribute, inputType: PROPOSALS_INPUT_TYPE};
            }
            return attribute;
        });
